import type { TFunction } from 'i18next';
import { formatInServerTz } from '../../utils/serverTime';

export type EvohomeSetpointModeUi =
  | 'follow_schedule'
  | 'temporary_override'
  | 'permanent_override';

/** Normaliza valores TCC / DB (FollowSchedule, temporary_override, …) al enum UI. */
export function normalizeEvohomeSetpointMode(
  mode: string | null | undefined,
): EvohomeSetpointModeUi | null {
  const m = String(mode || '')
    .toLowerCase()
    .replace(/-/g, '')
    .replace(/_/g, '');
  if (!m) return null;
  if (m.includes('follow') || m === 'followschedule') return 'follow_schedule';
  if (m.includes('temporary') || m === 'temporaryoverride') return 'temporary_override';
  if (m.includes('permanent') || m === 'permanentoverride') return 'permanent_override';
  return null;
}

function formatRemaining(until: Date, now = new Date()): string | null {
  const mins = Math.round((until.getTime() - now.getTime()) / 60000);
  if (!Number.isFinite(mins) || mins <= 0) return null;
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

/**
 * Etiqueta i18n del modo de zona Evohome.
 * Temporal incluye tiempo restante (preferido) o hora de fin.
 */
export function formatEvohomeSetpointModeLabel(
  t: TFunction,
  mode: string | null | undefined,
  untilTs?: string | null,
): string | null {
  const ui = normalizeEvohomeSetpointMode(mode);
  if (!ui) {
    const raw = String(mode || '').trim();
    return raw || null;
  }

  if (ui === 'temporary_override' && untilTs) {
    const until = new Date(untilTs);
    if (!Number.isNaN(until.getTime())) {
      const remaining = formatRemaining(until);
      if (remaining) {
        return t('evohome.mode.temporaryWithRemaining', {
          defaultValue: 'Temporal · {{remaining}}',
          remaining,
        });
      }
      const when = formatInServerTz(until, 'dd/MM HH:mm');
      return t('evohome.mode.temporaryWithUntil', {
        defaultValue: 'Temporal · {{when}}',
        when,
      });
    }
  }

  return t(`evohome.mode.${ui}`, {
    defaultValue:
      ui === 'follow_schedule' ? 'Programa'
        : ui === 'temporary_override' ? 'Temporal'
          : 'Permanente',
  });
}

/** Color de acento por modo (alineado al modal Evohome). */
export function evohomeSetpointModeColor(mode: string | null | undefined): string {
  const ui = normalizeEvohomeSetpointMode(mode);
  if (ui === 'temporary_override') return '#FFB74D';
  if (ui === 'permanent_override') return '#FF8A65';
  if (ui === 'follow_schedule') return '#90A4AE';
  return '#888';
}

/** Canonical snake_case for SystemMode values from TCC / DB. */
export function normalizeEvohomeSystemMode(mode: string | null | undefined): string {
  return String(mode || '')
    .trim()
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/-/g, '_')
    .toLowerCase();
}

/** Apagada: `heating_off` u `off`, con la misma normalización que el modo de sistema. */
export function isHeatingOff(mode: string | null | undefined): boolean {
  const n = normalizeEvohomeSystemMode(mode);
  return n === 'heating_off' || n === 'off';
}

export const isHeatingOffMode = isHeatingOff;

type SystemModeLabelKey =
  | 'auto'
  | 'eco'
  | 'reset'
  | 'away'
  | 'dayOff'
  | 'heatingOff'
  | 'custom';

function systemModeLabelKey(mode: string): SystemModeLabelKey | string {
  const n = normalizeEvohomeSystemMode(mode);
  const keys: Record<string, SystemModeLabelKey> = {
    auto: 'auto',
    auto_with_eco: 'eco',
    auto_with_reset: 'reset',
    away: 'away',
    day_off: 'dayOff',
    heating_off: 'heatingOff',
    off: 'heatingOff',
    custom: 'custom',
  };
  return keys[n] || n;
}

/**
 * Etiqueta i18n del modo de sistema Evohome (Auto / Eco / Ausente / …).
 * Reutiliza keys de settings.evohomeDiscovery.mode.*.
 */
export function formatEvohomeSystemModeLabel(
  t: TFunction,
  mode: string | null | undefined,
): string | null {
  const n = normalizeEvohomeSystemMode(mode);
  if (!n) return null;
  const key = systemModeLabelKey(n);
  const defaults: Record<string, string> = {
    auto: 'Auto',
    eco: 'Eco',
    reset: 'Reiniciar',
    away: 'Ausente',
    dayOff: 'Día libre',
    heatingOff: 'Apagada',
    custom: 'Personalizado',
  };
  return t(`settings.evohomeDiscovery.mode.${key}`, {
    defaultValue: defaults[key] || n,
  });
}

/** Color de acento del modo de sistema (chip de tarjeta clima). */
export function evohomeSystemModeColor(mode: string | null | undefined): string {
  const n = normalizeEvohomeSystemMode(mode);
  if (n === 'heating_off' || n === 'off') return '#EF5350';
  if (n === 'auto_with_eco') return '#81C784';
  if (n === 'away') return '#FFB74D';
  if (n === 'day_off') return '#4DD0E1';
  if (n === 'auto_with_reset') return '#CE93D8';
  if (n === 'custom') return '#B39DDB';
  if (n === 'auto') return '#90A4AE';
  return '#888';
}
